import React, { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./Onboarding.css";

// Типы оборудования для цены за единицу (Device.type + HardwareUnit.type). Ключи = как в БД.
const EQUIP_TYPES = ["CAMERA", "RF_SENSOR", "MASTER", "WIDEBAND", "SENSOR", "DETECTION_BOARD", "TURRET", "INTERCEPTOR", "NETGUN"];

const GET_BILLING = gql`
  query {
    partnersBilling {
      id name status subscriptionUntil pricePerObject billingCurrency billingInterval
      billingName billingEmail billingAddress billingTaxId
      unitPricing equipmentUnits equipmentAmount
      activeObjects monthlyAmount balanceDue openInvoices
    }
    billingMrr
    billingSettings { id companyName address taxId bankDetails invoicePrefix defaultCurrency footerNote }
  }
`;
const GET_INVOICES = gql`
  query($partnerId: ID) {
    invoices(partnerId: $partnerId) {
      id number partnerId periodStart periodEnd issueDate dueDate currency items subtotal total status paidAt
    }
  }
`;
const SET_PARTNER_BILLING = gql`
  mutation($partnerId: ID!, $pricePerObject: Int, $unitPricing: JSON, $billingCurrency: String, $billingInterval: String, $billingName: String, $billingEmail: String, $billingAddress: String, $billingTaxId: String) {
    setPartnerBilling(partnerId: $partnerId, pricePerObject: $pricePerObject, unitPricing: $unitPricing, billingCurrency: $billingCurrency, billingInterval: $billingInterval, billingName: $billingName, billingEmail: $billingEmail, billingAddress: $billingAddress, billingTaxId: $billingTaxId) { id }
  }
`;
const SET_SETTINGS = gql`
  mutation($companyName: String, $address: String, $taxId: String, $bankDetails: String, $invoicePrefix: String, $defaultCurrency: String, $footerNote: String) {
    setBillingSettings(companyName: $companyName, address: $address, taxId: $taxId, bankDetails: $bankDetails, invoicePrefix: $invoicePrefix, defaultCurrency: $defaultCurrency, footerNote: $footerNote) { id }
  }
`;
const GEN_INVOICE = gql`mutation($partnerId: ID!) { generateInvoice(partnerId: $partnerId) { id number } }`;
const UPD_STATUS = gql`mutation($id: ID!, $status: String!) { updateInvoiceStatus(id: $id, status: $status) { id status } }`;
const MARK_PAID = gql`mutation($id: ID!) { markInvoicePaid(id: $id) { id status } }`;
const DEL_INVOICE = gql`mutation($id: ID!) { deleteInvoice(id: $id) }`;

const money = (amount: number, cur: string) => (cur === "USD" ? "$" + (amount || 0).toLocaleString() : (amount || 0).toLocaleString() + " " + cur);
const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString() : "—";

export default function Billing() {
  const { data, loading, refetch } = useQuery(GET_BILLING);
  const [setBilling] = useMutation(SET_PARTNER_BILLING, { onCompleted: () => refetch() });
  const [setSettings] = useMutation(SET_SETTINGS, { onCompleted: () => refetch() });
  const [genInvoice] = useMutation(GEN_INVOICE);
  const [updStatus] = useMutation(UPD_STATUS);
  const [markPaid] = useMutation(MARK_PAID);
  const [delInvoice] = useMutation(DEL_INVOICE);

  const [msg, setMsg] = useState("");
  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };
  const run = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); flash(ok); } catch (e: any) { flash("Error: " + e.message); }
  };

  // pricing edit
  const [editId, setEditId] = useState<string>("");
  const [ef, setEf] = useState<any>({});
  const startEdit = (p: any) => {
    setEditId(p.id);
    const up: any = {}; EQUIP_TYPES.forEach(t => { up[t] = String((p.unitPricing && p.unitPricing[t]) || 0); });
    setEf({ pricePerObject: String(p.pricePerObject || 0), unitPricing: up, billingCurrency: p.billingCurrency || "USD", billingInterval: p.billingInterval || "MONTHLY", billingName: p.billingName || "", billingEmail: p.billingEmail || "", billingAddress: p.billingAddress || "", billingTaxId: p.billingTaxId || "" });
  };
  const saveEdit = () => {
    const unitPricing: any = {};
    EQUIP_TYPES.forEach(t => { const v = parseInt(String((ef.unitPricing || {})[t] || "0"), 10) || 0; if (v > 0) unitPricing[t] = v; });
    run(() => setBilling({ variables: {
      partnerId: editId, pricePerObject: parseInt(ef.pricePerObject || "0", 10) || 0, unitPricing,
      billingCurrency: ef.billingCurrency, billingInterval: ef.billingInterval,
      billingName: ef.billingName || null, billingEmail: ef.billingEmail || null,
      billingAddress: ef.billingAddress || null, billingTaxId: ef.billingTaxId || null,
    } }).then(() => setEditId("")), "Pricing saved");
  };

  // settings
  const s = data?.billingSettings;
  const [sf, setSf] = useState<any>(null);
  const sv = sf || (s ? { companyName: s.companyName || "", address: s.address || "", taxId: s.taxId || "", bankDetails: s.bankDetails || "", invoicePrefix: s.invoicePrefix || "INV", footerNote: s.footerNote || "" } : { companyName: "", address: "", taxId: "", bankDetails: "", invoicePrefix: "INV", footerNote: "" });
  const setSV = (k: string, v: string) => setSf({ ...sv, [k]: v });

  // invoices panel
  const [openPartner, setOpenPartner] = useState<string>("");
  const invQ = useQuery(GET_INVOICES, { variables: { partnerId: openPartner || null }, skip: !openPartner });

  const partners = data?.partnersBilling || [];

  const invoicePdf = (inv: any, partner: any) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const set = data?.billingSettings || {};
    // header
    doc.setFillColor(10, 22, 40); doc.rect(0, 0, W, 70, "F");
    doc.setTextColor(79, 195, 247); doc.setFont("helvetica", "bold"); doc.setFontSize(18);
    doc.text(set.companyName || "DroneDefender", 40, 32);
    doc.setTextColor(220, 235, 245); doc.setFont("helvetica", "normal"); doc.setFontSize(11);
    doc.text("INVOICE", 40, 52);
    doc.setFontSize(11); doc.setTextColor(40, 40, 40);
    doc.text("Invoice " + inv.number, W - 40, 32, { align: "right" });

    let y = 96;
    doc.setFontSize(9); doc.setTextColor(90, 90, 90);
    // From
    doc.setFont("helvetica", "bold"); doc.text("From:", 40, y);
    doc.setFont("helvetica", "normal");
    const fromLines = [set.companyName, set.address, set.taxId ? "Tax ID: " + set.taxId : null].filter(Boolean);
    fromLines.forEach((l: string, i: number) => doc.text(String(l), 40, y + 14 + i * 12));
    // Bill to
    doc.setFont("helvetica", "bold"); doc.text("Bill to:", W / 2, y);
    doc.setFont("helvetica", "normal");
    const toLines = [partner.billingName || partner.name, partner.billingAddress, partner.billingEmail, partner.billingTaxId ? "Tax ID: " + partner.billingTaxId : null].filter(Boolean);
    toLines.forEach((l: string, i: number) => doc.text(String(l), W / 2, y + 14 + i * 12));

    y += 14 + Math.max(fromLines.length, toLines.length) * 12 + 16;
    doc.setTextColor(40, 40, 40);
    doc.text("Issue date: " + fmtDate(inv.issueDate), 40, y);
    doc.text("Due date: " + fmtDate(inv.dueDate), 40, y + 12);
    doc.text("Period: " + fmtDate(inv.periodStart) + " — " + fmtDate(inv.periodEnd), 40, y + 24);
    doc.text("Status: " + inv.status, W - 40, y, { align: "right" });

    const items = Array.isArray(inv.items) ? inv.items : [];
    autoTable(doc, {
      startY: y + 40,
      head: [["Description", "Qty", "Unit (" + inv.currency + ")", "Amount (" + inv.currency + ")"]],
      body: items.map((it: any) => [it.description, String(it.qty), (it.unitPrice || 0).toLocaleString(), (it.amount || 0).toLocaleString()]),
      foot: [["", "", "Total", money(inv.total, inv.currency)]],
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [2, 136, 209], textColor: 255 },
      footStyles: { fillColor: [240, 247, 252], textColor: 20, fontStyle: "bold" },
      margin: { left: 40, right: 40 },
    });

    let endY = (doc as any).lastAutoTable.finalY + 24;
    doc.setFontSize(9); doc.setTextColor(60, 60, 60);
    if (set.bankDetails) {
      doc.setFont("helvetica", "bold"); doc.text("Payment details:", 40, endY);
      doc.setFont("helvetica", "normal");
      String(set.bankDetails).split("\n").forEach((l: string, i: number) => doc.text(l, 40, endY + 14 + i * 12));
      endY += 14 + String(set.bankDetails).split("\n").length * 12;
    }
    if (set.footerNote) { doc.setTextColor(120, 120, 120); doc.text(String(set.footerNote), 40, endY + 16); }

    doc.save(inv.number + ".pdf");
  };

  return (
    <div className="page-container onb">
      <div className="page-header"><h2>💳 Billing</h2></div>
      {msg && <div className="onb-msg">{msg}</div>}

      <div className="onb-metrics">
        <div className="onb-metric"><div className="m-val">{money(data?.billingMrr || 0, "USD")}</div><div className="m-lbl">MRR (recurring/mo)</div></div>
        <div className="onb-metric"><div className="m-val">{partners.length}</div><div className="m-lbl">Billable partners</div></div>
        <div className="onb-metric"><div className="m-val">{money(partners.reduce((a: number, p: any) => a + (p.balanceDue || 0), 0), "USD")}</div><div className="m-lbl">Outstanding balance</div></div>
      </div>

      <div className="onb-card">
        <h3>Vendor billing details (invoice header)</h3>
        <div className="onb-form" style={{ flexDirection: "column", alignItems: "stretch", maxWidth: 560 }}>
          <input placeholder="Company name" value={sv.companyName} onChange={e => setSV("companyName", e.target.value)} />
          <input placeholder="Address" value={sv.address} onChange={e => setSV("address", e.target.value)} />
          <input placeholder="Tax ID" value={sv.taxId} onChange={e => setSV("taxId", e.target.value)} />
          <textarea placeholder="Bank / payment details (multi-line)" rows={3} value={sv.bankDetails} onChange={e => setSV("bankDetails", e.target.value)} />
          <input placeholder="Invoice prefix (e.g. INV)" value={sv.invoicePrefix} onChange={e => setSV("invoicePrefix", e.target.value)} />
          <input placeholder="Footer note" value={sv.footerNote} onChange={e => setSV("footerNote", e.target.value)} />
          <button className="btn-primary" onClick={() => run(() => setSettings({ variables: sv }), "Settings saved")}>Save vendor details</button>
        </div>
      </div>

      <div className="onb-card">
        <h3>Partners</h3>
        {loading ? <div className="loading">Loading…</div> : (
          <table className="onb-table">
            <thead><tr><th>Partner</th><th>Objects</th><th>$/object</th><th>Interval</th><th>Equipment</th><th>Monthly</th><th>Balance</th><th>Subscription</th><th>Actions</th></tr></thead>
            <tbody>
              {partners.map((p: any) => (
                <React.Fragment key={p.id}>
                  <tr>
                    <td>{p.name}{p.status === "FROZEN" && <span className="onb-mini danger" style={{ marginLeft: 6 }}>FROZEN</span>}</td>
                    <td>{p.activeObjects}</td>
                    <td>{money(p.pricePerObject, p.billingCurrency)}</td>
                    <td>{p.billingInterval === "ANNUAL" ? "Annual" : "Monthly"}</td>
                    <td>{p.equipmentUnits ? <span>{money(p.equipmentAmount, p.billingCurrency)} <span className="onb-mini">{p.equipmentUnits}u</span></span> : "—"}</td>
                    <td>{money(p.monthlyAmount, p.billingCurrency)}</td>
                    <td>{p.balanceDue ? <b style={{ color: "#ff8a80" }}>{money(p.balanceDue, p.billingCurrency)}</b> : "—"}</td>
                    <td>{p.subscriptionUntil ? fmtDate(p.subscriptionUntil) : "Unlimited"}</td>
                    <td>
                      <button className="onb-mini" onClick={() => startEdit(p)}>Pricing</button>
                      <button className="onb-mini" onClick={() => run(() => genInvoice({ variables: { partnerId: p.id } }).then(() => { if (openPartner === p.id) invQ.refetch(); refetch(); }), "Invoice generated")}>Generate invoice</button>
                      <button className="onb-mini" onClick={() => setOpenPartner(openPartner === p.id ? "" : p.id)}>{openPartner === p.id ? "Hide invoices" : "Invoices"}</button>
                    </td>
                  </tr>
                  {editId === p.id && (
                    <tr><td colSpan={9}>
                      <div className="onb-form" style={{ background: "rgba(79,195,247,0.05)", padding: 12, borderRadius: 8 }}>
                        <input style={{ width: 110 }} placeholder="$/object" value={ef.pricePerObject} onChange={e => setEf({ ...ef, pricePerObject: e.target.value })} />
                        <div style={{ width: "100%", display: "flex", flexWrap: "wrap", gap: 8, margin: "6px 0" }}>
                          <span className="onb-mini" style={{ alignSelf: "center" }}>$/unit по типу:</span>
                          {EQUIP_TYPES.map(t => (
                            <label key={t} className="onb-mini" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              {t}
                              <input style={{ width: 64 }} type="number" min={0} value={(ef.unitPricing || {})[t] || ""} onChange={e => setEf({ ...ef, unitPricing: { ...(ef.unitPricing || {}), [t]: e.target.value } })} />
                            </label>
                          ))}
                        </div>
                        <select value={ef.billingCurrency} onChange={e => setEf({ ...ef, billingCurrency: e.target.value })}>
                          <option>USD</option><option>EUR</option><option>GBP</option><option>UAH</option><option>PLN</option>
                        </select>
                        <select value={ef.billingInterval} onChange={e => setEf({ ...ef, billingInterval: e.target.value })}>
                          <option value="MONTHLY">Monthly</option><option value="ANNUAL">Annual</option>
                        </select>
                        <input placeholder="Bill-to name" value={ef.billingName} onChange={e => setEf({ ...ef, billingName: e.target.value })} />
                        <input placeholder="Bill-to email" value={ef.billingEmail} onChange={e => setEf({ ...ef, billingEmail: e.target.value })} />
                        <input placeholder="Bill-to address" value={ef.billingAddress} onChange={e => setEf({ ...ef, billingAddress: e.target.value })} />
                        <input placeholder="Tax ID" value={ef.billingTaxId} onChange={e => setEf({ ...ef, billingTaxId: e.target.value })} />
                        <button className="btn-primary" onClick={saveEdit}>Save</button>
                        <button className="onb-mini" onClick={() => setEditId("")}>Cancel</button>
                      </div>
                    </td></tr>
                  )}
                  {openPartner === p.id && (
                    <tr><td colSpan={9}>
                      <div style={{ padding: "6px 0" }}>
                        {invQ.loading ? <div className="loading">Loading invoices…</div> : (
                          <table className="onb-table">
                            <thead><tr><th>Number</th><th>Period</th><th>Due</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead>
                            <tbody>
                              {(invQ.data?.invoices || []).map((inv: any) => (
                                <tr key={inv.id}>
                                  <td>{inv.number}</td>
                                  <td>{fmtDate(inv.periodStart)} – {fmtDate(inv.periodEnd)}</td>
                                  <td>{fmtDate(inv.dueDate)}</td>
                                  <td>{money(inv.total, inv.currency)}</td>
                                  <td><span className={"inv-status s-" + inv.status.toLowerCase()}>{inv.status}</span></td>
                                  <td>
                                    <button className="onb-mini" onClick={() => invoicePdf(inv, p)}>PDF</button>
                                    {inv.status === "DRAFT" && <button className="onb-mini" onClick={() => run(() => updStatus({ variables: { id: inv.id, status: "SENT" } }).then(() => invQ.refetch()), "Marked sent")}>Mark sent</button>}
                                    {inv.status !== "PAID" && inv.status !== "VOID" && <button className="onb-mini" onClick={() => run(() => markPaid({ variables: { id: inv.id } }).then(() => { invQ.refetch(); refetch(); }), "Paid — subscription extended")}>Mark paid</button>}
                                    {inv.status === "DRAFT" && <button className="onb-mini danger" onClick={() => run(() => delInvoice({ variables: { id: inv.id } }).then(() => invQ.refetch()), "Deleted")}>Delete</button>}
                                    {(inv.status === "SENT" || inv.status === "OVERDUE") && <button className="onb-mini" onClick={() => run(() => updStatus({ variables: { id: inv.id, status: "VOID" } }).then(() => { invQ.refetch(); refetch(); }), "Voided")}>Void</button>}
                                  </td>
                                </tr>
                              ))}
                              {(invQ.data?.invoices || []).length === 0 && <tr><td colSpan={6} className="no-data">No invoices yet — use “Generate invoice”.</td></tr>}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
              {partners.length === 0 && <tr><td colSpan={8} className="no-data">No billable partners yet. Create one in Manage.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
